import { MapDevice, MapCollection } from "../../../api/admin";

export type MapItem = MapDevice | MapCollection;

export type Cluster<T extends MapItem = MapItem> = {
  location: { latitude: number; longitude: number };
  devices: Array<T>;
};

export function calculateAverageLocation<T extends MapItem>(
  items: Array<T>,
): {
  latitude: number;
  longitude: number;
} {
  let totalLatitude = 0;
  let totalLongitude = 0;
  let count = 0;

  for (const item of items) {
    if (!item.randomLocation) continue;
    totalLatitude += item.randomLocation.latitude;
    totalLongitude += item.randomLocation.longitude;
    count++;
  }

  if (count === 0) {
    return { latitude: 0, longitude: 0 };
  }

  return {
    latitude: totalLatitude / count,
    longitude: totalLongitude / count,
  };
}

export function getRandomLocation(
  latitude: number,
  longitude: number,
  radiusInMeters: number,
): { latitude: number; longitude: number } {
  function getRandomCoordinates(radius: number, uniform: boolean) {
    let a = Math.random();
    let b = Math.random();

    if (uniform && b < a) {
      [a, b] = [b, a];
    }

    return [
      b * radius * Math.cos((2 * Math.PI * a) / b),
      b * radius * Math.sin((2 * Math.PI * a) / b),
    ];
  }

  const randomCoordinates = getRandomCoordinates(radiusInMeters, true);
  const earth = 6378137;

  const northOffset = randomCoordinates[0];
  const eastOffset = randomCoordinates[1];

  const offsetLatitude = northOffset / earth;
  const offsetLongitude =
    eastOffset / (earth * Math.cos(Math.PI * (latitude / 180)));

  return {
    latitude: latitude + offsetLatitude * (180 / Math.PI),
    longitude: longitude + offsetLongitude * (180 / Math.PI),
  };
}

export function clusterDevices<T extends MapItem>(
  items: Array<T>,
  latThreshold: number,
  lngThreshold: number,
): Array<T | Cluster<T>> {
  if (items.length === 0) return [];
  const result: Array<T | Cluster<T>> = [];
  const processed = new Set<T>();

  for (const item of items) {
    if (processed.has(item)) continue;

    const nearby = items.filter(
      (other) =>
        !processed.has(other) &&
        other.randomLocation &&
        item.randomLocation &&
        Math.abs(other.randomLocation.latitude - item.randomLocation.latitude) <
          latThreshold &&
        Math.abs(
          other.randomLocation.longitude - item.randomLocation.longitude,
        ) < lngThreshold,
    );

    nearby.forEach((d) => processed.add(d));

    if (nearby.length === 1) {
      result.push(nearby[0]);
    } else {
      result.push({
        devices: nearby,
        location: calculateAverageLocation(nearby),
      });
    }
  }

  return result;
}

export function calculatePixelThresholds(
  bounds: google.maps.LatLngBounds,
  mapSize: { width: number; height: number },
  markerSize: number = 24,
): { latPixels: number; lngPixels: number } {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  return {
    latPixels: (Math.abs(ne.lat() - sw.lat()) / mapSize.width) * markerSize,
    lngPixels: (Math.abs(ne.lng() - sw.lng()) / mapSize.height) * markerSize,
  };
}
